import { Signal, inject } from '@angular/core';
import { ToSignalOptions, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { Observable, filter, map, switchMap } from 'rxjs';

export type SignalValue<T> = T extends Signal<infer U> ? U : never;

export function signalAsync<S, O>(signal: Signal<S>, observable: (value: S) => Observable<O>, options: ToSignalOptions<any> = {}) {
  return toSignal(toObservable(signal, options).pipe(switchMap(value => observable(value))), options as any) as Signal<O>;
}

export type RouteParams = Record<string, string | undefined>;

export function getRouteParams(route: ActivatedRoute): RouteParams {
  let params = route.snapshot.params;
  let parent = route.parent;
  while (parent) {
    params = { ...parent.snapshot.params, ...params };
    parent = parent.parent;
  }
  return params;
}

export function injectQueryParams(): Signal<RouteParams> {
  const route = inject(ActivatedRoute);
  return toSignal(route.queryParams, { initialValue: route.snapshot.queryParams });
}

export function injectQueryParam(key: string): Signal<string | undefined> {
  const route = inject(ActivatedRoute);
  return toSignal(route.queryParams.pipe(map(params => params[key])), { initialValue: route.snapshot.queryParams[key] });
}

export function injectRouteParams() {
  const route = inject(ActivatedRoute);
  const params = getRouteParams(route);
  return toSignal(route.params.pipe(map(() => getRouteParams(route))), { initialValue: params });
}

export function injectRouteParam(key: string) {
  const route = inject(ActivatedRoute);
  const params = getRouteParams(route);
  return toSignal(route.params.pipe(map(() => getRouteParams(route)[key])), { initialValue: params[key] });
}

export function currentRoute() {
  const router = inject(Router);
  return toSignal(
    router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      map(() => router.url),
    ),
    { initialValue: router.url },
  );
}
